/**
 * The device's own local authentication, wrapped.
 *
 * Face ID / Touch ID on iOS, BiometricPrompt on Android, both via
 * `@aparajita/capacitor-biometric-auth`, which maps onto
 * `LAPolicy.deviceOwnerAuthentication` and `BiometricPrompt` respectively.
 *
 * Two rules this module exists to enforce:
 *
 * 1. `allowDeviceCredential: true`, always. Biometric-only is not shippable —
 *    enrolment fails, is impossible for some people, and iOS locks biometry out
 *    after repeated failures. The device passcode is the fallback the OS itself
 *    expects to offer, and Apple's platform guidance says so.
 * 2. A USER CANCEL IS NOT A FAILURE. iOS raises `systemCancel` for an incoming
 *    call or the screen locking, and `userCancel` when somebody dismisses the
 *    sheet. Counting those toward the lockout that wipes the session is how a
 *    person who takes three phone calls loses their offline data. Only a real
 *    biometric mismatch or a rejected passcode counts.
 */
import { isCapacitor } from '../api/config';

/** What this device can do, and therefore what the lock can promise. */
export interface BiometricCapability {
  /** Some form of local authentication — biometry OR a device passcode — exists. */
  canAuthenticate: boolean;
  /** Biometry specifically is enrolled and usable. */
  biometryEnrolled: boolean;
  /** The device has a passcode/PIN/pattern, so the fallback path works. */
  deviceIsSecure: boolean;
  /** `faceId` | `touchId` | `fingerprintAuthentication` | … | `none`. */
  biometryType: string;
  /** Machine-readable reason when biometry is unavailable; '' when it is. */
  code: string;
}

export const NO_CAPABILITY: BiometricCapability = {
  canAuthenticate: false,
  biometryEnrolled: false,
  deviceIsSecure: false,
  biometryType: 'none',
  code: 'biometryNotAvailable',
};

/** How an unlock attempt ended. */
export type UnlockOutcome =
  /** Identity proven — by biometry or by the device passcode. */
  | { kind: 'unlocked' }
  /**
   * The person walked away from the prompt, or the OS took it away. Costs
   * nothing: we stay locked and offer the prompt again.
   */
  | { kind: 'cancelled'; code: string }
  /**
   * The device said no — a face/finger it did not recognise, or a rejected
   * passcode. This is the only outcome that counts toward the lockout.
   */
  | { kind: 'failed'; code: string };

/**
 * Error codes that mean "the attempt was abandoned", not "the attempt was
 * refused". Kept as an explicit set rather than a negation, so a code this
 * plugin adds later defaults to the SAFE reading (counts as a failure and
 * eventually forces a full login) rather than to a free retry.
 */
const CANCEL_CODES = new Set([
  'userCancel',
  'appCancel',
  'systemCancel',
  'userFallback',
  'notInteractive',
  'invalidContext',
]);

type BiometricAuthPluginShape = {
  checkBiometry: () => Promise<{
    isAvailable: boolean;
    deviceIsSecure: boolean;
    biometryType: number | string;
    code: string;
  }>;
  authenticate: (options: Record<string, unknown>) => Promise<void>;
};

let pluginPromise: Promise<BiometricAuthPluginShape> | null = null;

/** Lazily imported so the web bundle never carries native-only code. */
async function getPlugin(): Promise<BiometricAuthPluginShape> {
  if (!pluginPromise) {
    pluginPromise = import('@aparajita/capacitor-biometric-auth').then(
      (mod) => mod.BiometricAuth as unknown as BiometricAuthPluginShape
    );
  }
  return pluginPromise;
}

/** Numeric `BiometryType` -> the name we show and store. */
const BIOMETRY_TYPE_NAMES = [
  'none',
  'touchId',
  'faceId',
  'fingerprintAuthentication',
  'faceAuthentication',
  'irisAuthentication',
];

function biometryTypeName(value: number | string): string {
  if (typeof value === 'string') return value;
  return BIOMETRY_TYPE_NAMES[value] ?? 'none';
}

/**
 * What this device can do. Never throws: an unreachable plugin is reported as
 * "cannot authenticate", which the policy layer then interprets — a device that
 * cannot lock is a real state the settings screen has to show, not an error to
 * swallow.
 */
export async function checkBiometricCapability(): Promise<BiometricCapability> {
  if (!isCapacitor) return NO_CAPABILITY;
  try {
    const plugin = await getPlugin();
    const result = await plugin.checkBiometry();
    return {
      // Either half is enough to lock behind: biometry, or the passcode
      // fallback on its own. A device with neither cannot be locked at all.
      canAuthenticate: result.isAvailable || result.deviceIsSecure,
      biometryEnrolled: result.isAvailable,
      deviceIsSecure: result.deviceIsSecure,
      biometryType: biometryTypeName(result.biometryType),
      code: result.code ?? '',
    };
  } catch {
    return NO_CAPABILITY;
  }
}

export interface UnlockPromptCopy {
  reason: string;
  cancelTitle: string;
  androidTitle: string;
  androidSubtitle: string;
}

/**
 * Present the system prompt and classify the result.
 *
 * Callers must not invoke this while the app is backgrounded — the OS resolves
 * it as `appCancel` immediately and the person returns to a lock screen that
 * looks like it already refused them.
 */
export async function promptUnlock(copy: UnlockPromptCopy): Promise<UnlockOutcome> {
  if (!isCapacitor) {
    return { kind: 'failed', code: 'biometryNotAvailable' };
  }
  try {
    const plugin = await getPlugin();
    await plugin.authenticate({
      reason: copy.reason,
      cancelTitle: copy.cancelTitle,
      androidTitle: copy.androidTitle,
      androidSubtitle: copy.androidSubtitle,
      // The passcode fallback. Without it a person whose face is not recognised
      // — a mask, a dark room, sunglasses — has no way back into the app.
      allowDeviceCredential: true,
      // Prefer Class 3 biometry where the device has it: weak 2D face unlock on
      // many Android handsets is spoofable with a photograph. The plugin
      // documents that Android 9-10 silently degrade this to weak when
      // allowDeviceCredential is set, which is an OS limitation, not ours.
      androidBiometryStrength: 1,
    });
    return { kind: 'unlocked' };
  } catch (error) {
    const code =
      typeof error === 'object' && error !== null && 'code' in error
        ? String((error as { code: unknown }).code)
        : 'authenticationFailed';
    return CANCEL_CODES.has(code)
      ? { kind: 'cancelled', code }
      : { kind: 'failed', code };
  }
}

/** Reset module state between tests. Not used by application code. */
export function __resetBiometricGateForTests(): void {
  pluginPromise = null;
}
